// Отчёты об ошибках клиента (E01-T10, tech/03 §17, tech/09 §3.9, ux/11 §3.7).
// Ловим исключения, необработанные промисы и потерю контекста WebGL. Каждую ошибку: (1) в очередь
// POST /client-errors (≤ 20 за раз, без подписи, с JWT), (2) событие аналитики client_error с fingerprint,
// (3) в GlitchTip, если задан DSN. initData вырезается из всех строк до отправки (tech/08 §2, Т-К-20).
import { scrubInitData } from '../boot/init-data.js';
import { track } from '../analytics/analytics.js';
import { storage } from '../state/storage.js';
import { ApiCallError } from '../net/api-client.js';

export type ErrorKind = 'js' | 'promise' | 'webgl' | 'asset' | 'network';

export interface QueuedError { kind: string; message: string; stack: string; screen: string; build: string }

export const MAX_BATCH = 20;
const STORE_KEY = 'pendingErrors';

/** FNV-1a 32 бит — дважды с разной солью: 16 hex-символов, хватает для группировки. */
function fnv(s: string, seed: number): string {
  let h = seed >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, '0');
}

/** Хеш стека без изменчивых частей (номера строк, адреса, числа): одна и та же ошибка — один fingerprint. */
export function fingerprint(kind: string, message: string, stack: string): string {
  const frames = stack.split('\n').slice(0, 5).map((l) => l.replace(/https?:\/\/[^\s)]+/g, '').replace(/:\d+:\d+/g, '')).join('|');
  const norm = `${kind}|${message.replace(/\d+/g, '#')}|${frames}`;
  return fnv(norm, 0x811c9dc5) + fnv(norm, 0x01000193);
}

export interface ReporterDeps {
  build: string;
  screen: () => string;
  /** Отправка пачки; бросает, если не удалось (пачка останется в очереди). */
  send: (sessionId: string, errors: QueuedError[]) => Promise<void>;
  /** Есть ли сессия: без JWT /client-errors не принимает, копим. */
  ready: () => boolean;
  onFatal: (code: string) => void;
  glitchtipDsn?: string;
  fetch?: typeof fetch;
}

export class ErrorReporter {
  readonly sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
  private queue: QueuedError[] = storage.getJson<QueuedError[]>(STORE_KEY) ?? [];
  private sending = false;
  lastFatal: string | null = null;

  constructor(private deps: ReporterDeps) {}

  report(kind: ErrorKind, err: unknown, o: { fatal?: boolean } = {}): string {
    const e = err instanceof Error ? err : new Error(typeof err === 'string' ? err : JSON.stringify(err));
    const message = scrubInitData(e.message).slice(0, 2000);
    const stack = scrubInitData(e.stack ?? '').slice(0, 16_000);
    const fp = fingerprint(kind, message, stack);
    const screen = this.deps.screen();
    this.queue.push({ kind, message, stack, screen, build: this.deps.build });
    if (this.queue.length > 100) this.queue.splice(0, this.queue.length - 100);
    track('client_error', { kind, message: message.slice(0, 300), screen, fingerprint: fp, fatal: !!o.fatal });
    this.toGlitchtip(kind, message, stack, fp, !!o.fatal);
    if (o.fatal) {
      this.lastFatal = fp;
      storage.setJson(STORE_KEY, this.queue); // экран перезапустят — отчёт уйдёт со следующего входа
      this.deps.onFatal(fp);
    }
    void this.flush();
    return fp;
  }

  pendingCount(): number { return this.queue.length; }

  async flush(): Promise<void> {
    if (this.sending || !this.queue.length || !this.deps.ready()) return;
    this.sending = true;
    try {
      while (this.queue.length) {
        const batch = this.queue.slice(0, MAX_BATCH);
        await this.deps.send(this.sessionId, batch);
        this.queue.splice(0, batch.length);
      }
      storage.remove(STORE_KEY);
    } catch { /* сеть — попробуем при следующей ошибке или входе */ } finally {
      this.sending = false;
    }
  }

  /** Минимальная отправка в Sentry-совместимый GlitchTip (envelope API), если DSN выдан окружением. */
  private toGlitchtip(kind: string, message: string, stack: string, fp: string, fatal: boolean): void {
    const dsn = this.deps.glitchtipDsn;
    if (!dsn) return;
    try {
      const u = new URL(dsn);
      const project = u.pathname.replace(/^\//, '');
      const url = `${u.protocol}//${u.host}/api/${project}/envelope/?sentry_key=${u.username}&sentry_version=7`;
      const eventId = crypto.randomUUID().replace(/-/g, '');
      const event = {
        event_id: eventId, platform: 'javascript', level: fatal ? 'fatal' : 'error', release: this.deps.build,
        fingerprint: [fp], tags: { kind, screen: this.deps.screen() },
        exception: { values: [{ type: kind, value: message, stacktrace: { frames: stack.split('\n').slice(0, 30).map((l) => ({ function: l.trim() })) } }] },
      };
      const body = `${JSON.stringify({ event_id: eventId, dsn })}\n${JSON.stringify({ type: 'event' })}\n${JSON.stringify(event)}`;
      void (this.deps.fetch ?? fetch)(url, { method: 'POST', body, keepalive: true }).catch(() => {});
    } catch { /* неверный DSN — молча */ }
  }

  /** Глобальные обработчики. Исключение нашего кода → SCR-FATAL; чужих скриптов (telegram-web-app.js) — только отчёт. */
  install(target: Window = window): void {
    target.addEventListener('error', (ev: ErrorEvent) => {
      if (ev.target && ev.target !== target && (ev.target as HTMLElement).tagName) {
        this.report('asset', new Error(`не загрузился ${(ev.target as HTMLImageElement).src ?? ''}`));
        return;
      }
      // Вызовы Telegram в странице (TelegramGameProxy.receiveEvent до загрузки telegram-web-app.js) — не наш код.
      const tg = /TelegramGameProxy|TelegramWebviewProxy/.test(ev.message ?? '');
      const ours = !tg && (!ev.filename || ev.filename.startsWith(location.origin));
      this.report('js', ev.error ?? new Error(ev.message), { fatal: ours });
    }, true);
    target.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      // Ошибки API показывает сетевой слой по коду; здесь — только исключения кода.
      if (ev.reason instanceof ApiCallError) return;
      this.report('promise', ev.reason, { fatal: true });
    });
  }

  /** Потеря WebGL: ждём восстановления 5 с, иначе SCR-FATAL (ux/11 §4 ST-ERROR). */
  watchWebgl(canvas: HTMLCanvasElement, waitMs = 5000): void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    canvas.addEventListener('webglcontextlost', (ev) => {
      ev.preventDefault();
      this.report('webgl', new Error('webglcontextlost'));
      timer = setTimeout(() => this.report('webgl', new Error('webgl not restored'), { fatal: true }), waitMs);
    });
    canvas.addEventListener('webglcontextrestored', () => { if (timer) clearTimeout(timer); });
  }
}
